/** Toplantı grubu + toplantı tip tanımları (038, no-any). Backend DTO'larıyla birebir. */

import type { ActionInput, MeetingAction } from '../actions/types'
import type { MinuteItem, MinuteItemInput } from '../minuteItems/types'

export interface MeetingGroup {
  id: number
  /** Görünen grup numarası (ör. `TG-2026-0001`) — sunucu üretir, salt gösterim. */
  recordNumber: string
  name: string
  description: string | null
  /** Kayıt grubu (122). */
  groupId: number
  groupName: string | null
  recordStatus: number
  /**
   * O gruba bağlı AKTİF + <b>yayınlanmış</b> toplantı sayısı (backend hesaplar): taslak (360) ve arşivli
   * (362) sayılmaz — taslaklar ayrı "Taslaklar" kartında listelenir.
   */
  meetingCount: number
  /** Gruptaki toplam aksiyon (İptal/Arşivlendi hariç) — {@link actionStatusCounts} adetlerinin toplamı. */
  actionTotal: number
  /**
   * Aksiyon durum dağılımı: durum id → adet (JSON anahtarı string). Yalnız kaydı olan durumlar gelir;
   * eksik durum 0 sayılır. Durum adları REFERANS'tan dinamik çekilir (bkz. `useActionStatuses`).
   */
  actionStatusCounts: Record<string, number>
  /** Optimistic lock (update/status isteğinde geri gönderilir). */
  modDate: string
}

export interface MeetingGroupUpsert {
  groupId: number
  name: string
  description?: string | null
  /** Yalnız güncellemede (optimistic lock). */
  modDate?: string
}

/** Toplantı durumu (REFERANS): 360 Taslak, 361 Yayınlandı, 362 Arşivlendi. */
export const MEETING_STATUS = { DRAFT: 360, PUBLISHED: 361, ARCHIVED: 362 } as const

/**
 * Toplantı içerik bölümü (aktif REF_MEETING_DECISION_TYPE) — içerik listelerinin başlık + sırası (SORT_INDEX)
 * Toplantı Kayıt Tipi tablosundan dinamik gelir. Backend MeetingContentSectionDto ile birebir.
 */
export interface MeetingContentSection {
  id: number
  nameTr: string
  nameEn: string | null
  sortIndex: number
}

/** İçerik bölümü başlığı (dile-duyarlı): EN seçili ve EN dolu ise EN; aksi halde TR. */
export function contentSectionTitle(s: MeetingContentSection, lang: string): string {
  return lang.startsWith('en') && s.nameEn ? s.nameEn : s.nameTr
}

/** Grup drill-down + arşiv toplantı satırı (salt-okuma). */
export interface MeetingListItem {
  id: number
  /** Görünen toplantı numarası (ör. `TK-2026-0145`); numara yayında atanır → taslakta boş dizge. */
  recordNumber: string
  name: string
  meetingDate: string
  meetingLocation: string
  enteredUserId: number
  enteredUserName: string | null
  modDate: string
  /** Arşiv listesinde grup kolonu için (drill-down'da grubun adı). */
  groupName: string | null
  /** 360 Taslak / 361 Yayınlandı / 362 Arşivlendi. */
  meetingStatusId: number | null
}

/** Toplantı detayında katılımcı (044). Backend MeetingParticipantDto ile birebir. */
export interface MeetingParticipant {
  employeeId: string
  fullName: string
  firstName: string | null
  lastName: string | null
  jobTitleTr: string | null
  jobTitleEn: string | null
  businessMail: string | null
  /**
   * Unvan grubu kodu — listenin **sırasının** dayanağı (30.09.2026). Detay yanıtı zaten sıralı
   * gelir; alan yine de taşınır çünkü **düzenleme** ekranı kayıtlı katılımcıları seçiciye geri
   * yükler ve yeni biri eklendiğinde liste istemcide yeniden sıralanır.
   */
  employeeRoleGroupCode: string | null
  /**
   * Katılımcı bir **isim listesinden** eklendiyse o listenin kimliği (115). Gösterim değişmez
   * (katılımcılar tek tek görünür); alan **düzenlemede kayıpsız geri gönderim** için taşınır —
   * taşınmazsa ilgisiz bir düzenleme listeden gelen herkesin bağını sessizce kopardı.
   */
  managerListId?: number | null
}

/** Döküman eki metası (044). Backend AttachedFileDto ile birebir. */
export interface MeetingDocument {
  id: number
  fileName: string
  size: number
  mimeType: string
  extension: string
  /** Yükleyen kullanıcının görünen adı (CRE_BY → UserDirectory); yoksa null. */
  uploadedByName: string | null
  /** Yüklenme zamanı (CRE_DATE, ISO Instant). */
  uploadedAt: string
}

/** Toplantı (tutanak) detayı (044). İçerik listeleri/İşlem Geçmişi bu fazda kapsam dışı (tablo yok). */
export interface MeetingDetail {
  id: number
  /** Görünen toplantı numarası (ör. TK-2026-0145); yayınlanmamışta boş dizge. */
  recordNumber: string
  name: string
  /** LocalDateTime — başlangıç (tarih + başlangıç saati). */
  meetingDate: string
  /** LocalDateTime — bitiş (tarih + bitiş saati). */
  meetingEndDate: string
  meetingLocation: string
  meetingGroupId: number
  groupName: string | null
  enteredUserId: number | null
  enteredUserName: string | null
  /** Optimistic lock (güncelleme isteğinde geri gönderilir). */
  modDate: string
  /** 360 Taslak / 361 Yayınlandı / 362 Arşivlendi. Arşivli → salt-okuma (Düzenle gizli). */
  meetingStatusId: number | null
  participants: MeetingParticipant[]
  /** Dağıtım listesi (119) — katılımcılarla aynı biçim ve sıra; kişiler toplantıyı görmez. */
  distributionList?: MeetingParticipant[]
  documents: MeetingDocument[]
  /** Aksiyon içerik listesi (MEETING_MINUTES tip=Aksiyon). */
  actions: MeetingAction[]
  /** Gündem maddeleri (tip=Gündem; başlık+açıklama). */
  agenda: MinuteItem[]
  /** Prensip kararları (tip=Prensip). */
  principles: MinuteItem[]
  /** Kararlar (tip=Karar). */
  decisions: MinuteItem[]
}

/** Toplantı güncelleme gövdesindeki katılımcı (HR snapshot). Backend ParticipantInput ile birebir. */
export interface MeetingParticipantInput {
  employeeId: string
  firstName?: string | null
  lastName?: string | null
  businessMail?: string | null
  jobTitleTr?: string | null
  jobTitleEn?: string | null
  /** Kişi bir isim listesinden eklendiyse o listenin kimliği (115); backend ParticipantInput ile aynı. */
  managerListId?: number | null
}

/** PUT /management/meetings/{id} gövdesi (044 düzenleme). Grup/giren kullanıcı değiştirilmez. */
export interface MeetingUpdateRequest {
  name: string
  /** LocalDateTime "YYYY-MM-DDTHH:mm:ss" — başlangıç (tarih + başlangıç saati). */
  meetingDate: string
  /** LocalDateTime "YYYY-MM-DDTHH:mm:ss" — bitiş (tarih + bitiş saati). */
  meetingEndDate: string
  meetingLocation: string
  participants: MeetingParticipantInput[]
  /**
   * Dağıtım listesi (119) — gönderilmezse sunucu listeye dokunmaz; [] → hepsini kaldırır.
   * Yayınlanmış tutanakta yeni eklenen kişiye tutanak PDF'i ekli e-posta gider.
   */
  distributionList?: MeetingParticipantInput[]
  /** Optimistic lock (detaydan gelen modDate geri gönderilir). */
  modDate: string
  /** Aksiyon maddeleri (analiz 1.10) — opsiyonel; tam senkron. */
  actions?: ActionInput[]
  /** Gündem maddeleri (analiz 1.7) — opsiyonel; tam senkron. */
  agenda?: MinuteItemInput[]
  /** Prensip kararları (analiz 1.8) — opsiyonel; tam senkron. */
  principles?: MinuteItemInput[]
  /** Kararlar (analiz 1.9) — opsiyonel; tam senkron. */
  decisions?: MinuteItemInput[]
}

/** Backend Spring PagedModel biçimi (içerik + iç içe page meta). */
export interface PagedResponse<T> {
  content: T[]
  page: { size: number; number: number; totalElements: number; totalPages: number }
}

export interface MeetingGroupQuery {
  q?: string
  page?: number
  size?: number
  /** true → yalnız altında aktif toplantısı olan gruplar (Toplantılar kart ekranı; boş grup gizli). */
  withMeetings?: boolean
}

/**
 * İşlem Geçmişi tek değişiklik satırı (LOG_INFO'dan çözülmüş). {@link key} alan anahtarı → i18n etiketi
 * (`meetings.history.field.<key>`). {@link oldValue} null ise özet (yalnız {@link newValue} gösterilir);
 * ikisi de dolu ise "eski → yeni".
 */
export interface MeetingLogDetail {
  key: string
  oldValue: string | null
  newValue: string | null
}

/** İşlem Geçmişi (zaman tüneli) tek olay: işlem adı + kullanıcı + zaman + çözülmüş değişiklikler. */
export interface MeetingLogEntry {
  id: number
  operationCodeId: number
  operationName: string
  /** Kaydın sahibi (`CRE_BY`). Vekil modunda **adına çalışılan** kişidir. */
  userName: string | null
  /**
   * İşlemi **fiilen yapan** kişi — yalnız vekil olarak yapılmış işlemlerde dolu (089).
   * Doluysa meta satırı "X adına Y tarafından" olur; boşsa kullanıcı kendi kimliğiyle çalışmıştır.
   */
  actingUserName: string | null
  creDate: string
  details: MeetingLogDetail[]
}
